import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

import Header from 'components/Header';
import ConfirmModal from 'components/ConfirmModal';
import APIServices from 'services/APIServices';
import appToast, { TOAST_TYPE } from 'utils/AppToast';
import { useAuth } from 'contexts/AuthContext';
import { convertUnixToDate, selectLoader } from 'utils/Helpers';

import leftArrow from '../../../assets/img/left-arrow.svg';
import lovely from '../../../assets/img/lovely.svg';
import tickcircle from '../../../assets/img/tick-circle.svg';

import '../../../assets/css/plan-detail.css';
import SubPlan from 'pages/auth/welcome/SubPlan';

const PlanDetails = () => {
  const navigate = useNavigate();
  const { customerId, user, subInfo, socket } = useAuth();

  const [paymentMethods, setPaymentMethods] = useState([]);
  
  const [currentPlan, setCurrentPlan] = useState([]);
  const [pastPlan, setPastPlan] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchingMethods, setFetchingMethods] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [cancelSub, setCancelSub] = useState(false);
  const [isCancelling, setCancelling] = useState(false);
  const [isPlanUpdated, setPlanUpdated] = useState(false);
  const handleBackClick = () => navigate(-1);

  useEffect(() => {
    // Ensure Chargebee is initialized
    if (window.Chargebee && window.Chargebee.getInstance) {
      const cbInstance = window.Chargebee.getInstance();
      cbInstance.setCheckoutCallbacks({
        success: function (hostedPageId) {
          console.log('Checkout successful!', hostedPageId);
        },
        error: function (error) {
          console.error('Error during checkout:', error);
        }
      });
    }

    // Manually re-scan the DOM and attach event handlers after React renders
    if (window.Chargebee && window.Chargebee.registerAgain) {
      window.Chargebee.registerAgain(); // Reattach Chargebee handlers
    }
  }, [plans]);

  useEffect(() => {
    // Listen for 'projectActivityUpdate' event
    if (socket) {
      const handle_UserSubscriptionUpdated = (activity) => {
        fetchActiveSubscription();
        localStorage.setItem('isUpdatingPlan', true);
        // scrollToBottom();
        console.log('UserSubscriptionUpdated', activity);
      };
      socket?.on('UserSubscriptionUpdated', handle_UserSubscriptionUpdated);
      return () => {
        socket?.off('UserSubscriptionUpdated', handle_UserSubscriptionUpdated);
      };
    }
  }, [socket]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // const [subscriptionRes, plansRes, methodsRes] = await Promise.all([
        //   APIServices.get(`/subscription/${customerId}`),
        //   APIServices.get(`/subscription/current-plan/${user._id}`),
        //   APIServices.get(`/subscription/payment-methods/${customerId}`)
        // ]);
        const subscriptionRes = await fetchSubscription();
        const plansRes = await fetchActiveSubscription();
        const methodsRes = await fetchMethods();
        if (subscriptionRes?.current) {
          setCurrentPlan(subscriptionRes.current);
          setPastPlan(subscriptionRes.past);
        } else {
          appToast(subscriptionRes?.data?.message, TOAST_TYPE.ERROR);
        }
        console.log(plansRes);
        if (plansRes) {
          setPlans(plansRes.subscription);
        }
        if (methodsRes) {
          setPaymentMethods(methodsRes.paymentMethods);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const cancelSubscription = async () => {
    if (!selectedSubId) return;
    try {
      setCancelling(true);
      localStorage.setItem('isUpdatingPlan', true);
      const response = await APIServices.delete(`/subscription/${selectedSubId}/cancel`);
      const message = response.message || response?.data?.message;
      if (response.message) {
        appToast(message, response.message ? TOAST_TYPE.SUCCESS : TOAST_TYPE.ERROR);
        setCancelSub(false);
        setSelectedSubId('');
        navigate('/subscription-cancelled');
      } else if (response?.data?.message) {
        appToast(message, response.message ? TOAST_TYPE.SUCCESS : TOAST_TYPE.ERROR);
      }
      // await fetchActiveSubscription();
      // await fetchSubscription(); // Refresh after cancel
    } catch (error) {
      console.error('Cancellation error:', error);
      appToast('Failed to cancel subscription', TOAST_TYPE.ERROR);
    } finally {
      setCancelling(false);
    }
  };

  const removePaymentMethod = async () => {
    if (!paymentId) return;
    try {
      setCancelling(true);
      const response = await APIServices.delete(`/subscription/payment-methods/${paymentId}/`);
      const message = response.message || response?.data?.message;
      appToast(message, response.message ? TOAST_TYPE.SUCCESS : TOAST_TYPE.ERROR);
      setCancelSub(false);
      setPaymentId('');
      await fetchMethods(); // Refresh after cancel
    } catch (error) {
      console.error('Cancellation error:', error);
      appToast('Failed to remove payment method.', TOAST_TYPE.ERROR);
    } finally {
      setCancelling(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await APIServices.get(`/subscription/${customerId}`);
      if (response?.current) {
        setCurrentPlan(response.current);
        setPastPlan(response.past);
      }
      return response;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      appToast('Failed to load plan details. Please refresh and try again.', TOAST_TYPE.ERROR);
    }
  };

  const fetchActiveSubscription = async () => {
    if (!subInfo?.isActive) return;
    try {
      const response = await APIServices.get(`/subscription/current-plan/${user._id}`);
      if (response?.current) {
        setPlans(response.subscription);
      }
      return response;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      appToast('Failed to load plan details. Please refresh and try again.', TOAST_TYPE.ERROR);
    }
  };

  const fetchMethods = async () => {
    try {
      setFetchingMethods(true);
      const response = await APIServices.get(`/subscription/payment-methods/${customerId}`);
      if (response?.paymentMethods) {
        setPaymentMethods(response?.paymentMethods);
      }
      return response;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      appToast('Failed to load plan details. Please refresh and try again.', TOAST_TYPE.ERROR);
    } finally {
      setFetchingMethods(false);
    }
  };

  const downloadingInvoiceRef = useRef(false);
  const downloadInvoice = async (subId) => {
    if (downloadingInvoiceRef.current) return;
    downloadingInvoiceRef.current = true;
    try {
      const response = await APIServices.get(`/subscription/${subId}/invoice`);
      if (response.downloadUrl) {
        const { downloadUrl } = response;

        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank'; // Optional: Open in a new tab
        link.download = 'quote.pdf'; // Optional: Suggest a filename for the download

        // Append the link to the body and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up the DOM
        document.body.removeChild(link);
      }
      // const message = response.message || response?.data?.message;
      // appToast(message, response.message ? TOAST_TYPE.SUCCESS : TOAST_TYPE.ERROR);
    } catch (error) {
      console.error('Invoice download error:', error);
      appToast('Failed to download invoice', TOAST_TYPE.ERROR);
    } finally {
      downloadingInvoiceRef.current = false;
    }
  };

  if (isLoading) {
    return <div className="nodata-loader">{selectLoader(50)}</div>;
  }

  const PlanCard = ({ plan }) => {
    const price = (plan?.price || 0) / 100;
    return (
      <div className="plan-card-box plan-card-box-width" key={plan?.id}>
        <div className="plan-content-head">
          <img src={lovely} alt="lovely" className="lovely" />
          <h2>{plan?.name}</h2>
        </div>
        {!plan?.isInTrial && (
          <span>
            ${price.toFixed(2)} <sub>/month</sub>
          </span>
        )}
        <ul
          className="plan-list"
          dangerouslySetInnerHTML={{
            __html: plan?.description
              ?.replace(/<li>/g, `<li class="dark-text"><img src="${tickcircle}" alt="tick" class="tickcircle"/><p>`)
              .replace(/<\/li>/g, '</p></li>')
          }}
        />
        {/* <a
          href={plan.redirectUrl}
          className="btn-black"
          data-cb-type="checkout"
          data-cb-item-0={plan.prices?.[0]?.id}
          data-cb-item-0-quantity="1">
          Get Started
        </a> */}
      </div>
    );
  };

  const cancelSubContent = (
    <>
      <h2 className="mb-3">Are you sure?</h2>
      <h3>You want to cancel this subscription?</h3>
    </>
  );

  const removePaymentContent = (
    <>
      <h2 className="mb-3">Are you sure?</h2>
      <h3>You want to remove this payment method?</h3>
    </>
  );

  const handleAddPaymentMethod = () => {
    const cbInstance = window.Chargebee.getInstance();

    cbInstance.openCheckout({
      hostedPage: async () => {
        try {
          const payload = { customerId: customerId };
          const response = await APIServices.post(`/subscription/create-payment-hosted-page`, payload);

          // You must return just the hosted page object here
          return response.hostedPage || response;
        } catch (error) {
          console.error('Error creating hosted page:', error);
          throw error;
        }
      },
      success: (hostedPage) => {
        console.log('✅ Payment method added!');
        console.log('Hosted Page:', hostedPage);
        console.log('Token:', hostedPage.payment_method?.tmp_token || 'No token (card saved directly)');
      },
      error: (err) => {
        console.error('❌ Error in checkout:', err);
      },
      close: () => {
        fetchMethods();
        console.log('🔙 User closed the Add Payment Method UI without completing.');
      }
    });
  };

  return (
    <div className="wrapper sub-section">
        <Header
          content={
            <>
              <h2>My Plan</h2>
              <div className="head-right">
                <a onClick={handleBackClick}>
                  <img src={leftArrow} alt="back" className="left-arrow" />
                </a>
              </div>
            </>
          }
        />
      <div className="main-content">
      

        <section className="plan-sec pb-5">
          <div className="container">
            <div className="plan-head-content">
              {subInfo?.planDetails?.external_name && subInfo?.isActive && subInfo?.status === 'active' ? (
                <>
                  <h2>Your Plan</h2>
                  <p>
                    Your workspace is currently on the <strong>{subInfo?.planDetails?.external_name}</strong> Plan.
                  </p>
                </>
              ) : subInfo?.status === 'in_trial' ? (
                <>
                  <h2>14 day trial</h2>
                  <p>Your trial will expire after 14 days</p>
                </>
              ) : (
                <>
                  <h2>No Active Plan</h2>
                  <p>Subscribe now and get the Starter Plan</p>
                </>
              )}
            </div>

            <div className="row justify-content-center">
              <div className="col-12">
                {/* {plans.map((plan) => ( */}
                {plans?.planDetails && subInfo?.isActive ? (
                  <PlanCard plan={plans?.planDetails} />
                ) : (
                  <SubPlan isHome={true} />
                )}
                {/* ))} */}
              </div>
            </div>

            <div className="plan-head-content">
              <h2>Billing</h2>
            </div>
            <div className="row justify-content-center add-on-sec">
              <div className="col-12">
                {currentPlan?.map((plan, i) => {
                  const monthlyPrice = (plan?.planDetails?.price || 0) / 100;
                  return (
                    <div className="plan-card-box" key={i}>
                      <div className="plan-content-cover">
                        <div className="plan-content-left">
                          <h2>Renewal Information</h2>
                        </div>
                        {plan?.status !== 'in_trial' && <div className="plan-content-right">
                          <button
                            className="btn-cancel"
                            onClick={() => {
                              setSelectedSubId(plan.id);
                              setCancelSub(true);
                            }}>
                            Cancel Plan
                          </button>
                        </div>}
                      </div>
                      <div className="row row-cols-1 row-cols-md-5 justify-content-between">
                        <div className="col">
                          <div className="information-content">
                            <p>Your Plan</p>
                            <label>{plan?.name}</label>
                          </div>
                        </div>
                        <div className="col">
                          <div className="information-content">
                            <p>Billing Cycle</p>
                            <label>{plan?.planDetails.period_unit === 'month' ? 'Monthly' : 'Yearly'}</label>
                          </div>
                        </div>
                        <div className="col">
                          <div className="information-content">
                            <p>Next Billing Date</p>
                            {/* {console.log(plan)
                            } */}
                            <label>{convertUnixToDate(plan?.nextBillingAt)}</label>
                          </div>
                        </div>
                        <div className="col">
                          <div className="information-content">
                            <p>Total</p>
                            <label>${monthlyPrice}</label>
                          </div>
                        </div>
                        {plan?.status !== 'in_trial' ? (
                          <div className="col">
                            <div className="information-content">
                              <button className="btn-black" onClick={() => downloadInvoice(plan.id)}>
                                Invoice Download
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="col">
                            <div className="information-content text-end">
                              <button
                                className="btn-cancel"
                                onClick={() => {
                                  setSelectedSubId(plan.id);
                                  setCancelSub(true);
                                }}>
                                Cancel Plan
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="row justify-content-center add-on-sec">
              <div className="col-12">
                <div className="plan-card-box mt-20">
                  <div className="plan-content-cover">
                    <div className="plan-content-left">
                      <h2>Payment method</h2>
                    </div>
                    <div className="plan-content-right">
                      <button className="plan-add-btn" onClick={handleAddPaymentMethod}>
                        +Add New
                      </button>
                    </div>
                  </div>
                  {fetchingMethods ? (
                    <div className="text-center p-3">{selectLoader(60)}</div>
                  ) : paymentMethods?.length > 0 ? (
                    paymentMethods.map((method) => (
                      <div key={method.id} className="row row-cols-1 row-cols-md-3 justify-content-between mb-3">
                        <div className="col">
                          <div className="information-content">
                            <p>Card</p>
                            <label>
                              {method.card.brand} ****{method.card.last4}
                            </label>
                          </div>
                        </div>
                        <div className="col">
                          <div className="information-content">
                            <p>Expires</p>
                            <label>
                              {String(method.card.expiry_month).padStart(2, '0')}/{method.card.expiry_year}
                            </label>
                          </div>
                        </div>
                        <div className="col">
                          <div className="information-content">
                            <button
                              className="btn-remove"
                              onClick={() => {
                                setPaymentId(method.id);
                                setCancelSub(true);
                              }}>
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-3">No payment methods found.</div>
                  )}
                </div>
              </div>
            </div>

            {pastPlan.length > 0 && (
              <div className="plan-head-content">
                <h2>Invoices</h2>
              </div>
            )}
            {pastPlan.length > 0 &&
              pastPlan.map((plan, i) => {
                const price = (plan?.planDetails?.price || 0) / 100;
                return (
                  <div key={i} className="row justify-content-center add-on-sec invoices">
                    <div className="col-12">
                      <div className="plan-card-box">
                        <div className="row row-cols-1 row-cols-md-4 justify-content-between">
                          <div className="col">
                            <div className="information-content">
                              <p>Date</p>
                              <label>{convertUnixToDate(plan?.activated_at)}</label>
                            </div>
                          </div>
                          <div className="col">
                            <div className="information-content">
                              <p>Invoice</p>
                              <label>{plan?.id}</label>
                            </div>
                          </div>
                          <div className="col">
                            <div className="information-content">
                              <p>Amount</p>
                              <label>${price}</label>
                            </div>
                          </div>
                          {plan?.status !== 'in_trial' && (
                            <div className="col">
                              <div className="information-content">
                                <button className="btn-black btn-black-ml" onClick={() => downloadInvoice(plan.id)}>
                                  Invoice Download
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>

        <ConfirmModal
          show={cancelSub}
          onHide={() => {
            setCancelSub(false);
            setSelectedSubId('');
            setPaymentId('');
          }}
          confirmationText={paymentId ? removePaymentContent : cancelSubContent}
          handleAction={paymentId ? removePaymentMethod : cancelSubscription}
          closeText="Yes"
          loading={isCancelling}
        />
      </div>
    </div>
  );
};

export default PlanDetails;
